/**
 * @author Team CMS
 *
 * Hook to handle video messages from different video platforms.
 */
'use client';

// import { trackEvent } from '@svenskaspelab/nova-core/analytics/nextjs';
import { createLogger } from '@svenskaspelab/nova-core/log/nextjs';
import { useCallback, useEffect } from 'react';
import { eventNotifier } from '../../../core/utils/eventNotifier.js';
import '../VideoPlayer.types.js';
const logger = createLogger('useVideoMessageHandler');
const videoStates = new Map();
function dispatchCustomEvent(eventName, detail) {
  if (!eventNotifier) {
    return;
  }
  eventNotifier.dispatchEvent(new CustomEvent(eventName, {
    detail
  }));
}
/* eslint-disable-next-line @typescript-eslint/no-unused-vars */
function sendTrackEvent(action, category, videoId, videoTitle, optValue) {
  // trackEvent({
  //   action,
  //   category,
  //   optLabel: `${videoId} - ${videoTitle}`,
  //   optValue: optValue,
  // });
}
function handleVideoStart(videoId, videoTitle) {
  if (!videoStates.has(videoId)) {
    sendTrackEvent('Video start-youtube', 'Youtube video', videoId, videoTitle, 1);
    const timer = setTimeout(() => {
      sendTrackEvent('Video progress-youtube', 'Youtube video', videoId, videoTitle, 1);
      videoStates.delete(videoId);
    }, 30000);
    videoStates.set(videoId, {
      timer,
      startTime: Date.now(),
      accumulatedTime: 0
    });
  } else {
    const videoState = videoStates.get(videoId);
    if (videoState) {
      videoState.startTime = Date.now();
      if (!videoState.timer) {
        const remainingTime = 30000 - videoState.accumulatedTime * 1000;
        videoState.timer = setTimeout(() => {
          sendTrackEvent('Video progress-youtube', 'Youtube video', videoId, videoTitle, 1);
          videoStates.delete(videoId);
        }, remainingTime);
      }
    }
  }
}
function handleVideoPauseOrEnd(videoId, videoTitle) {
  const videoState = videoStates.get(videoId);
  if (videoState) {
    const playTime = (Date.now() - videoState.startTime) / 1000; // Convert ms to seconds
    videoState.accumulatedTime += playTime;
    if (videoState.accumulatedTime >= 30) {
      sendTrackEvent('Video progress-youtube', 'Youtube video', videoId, videoTitle, 1);
      videoStates.delete(videoId);
    } else {
      if (videoState.timer) clearTimeout(videoState.timer);
      videoState.timer = null;
    }
  }
}
function handleYouTubeMessage(videoId, videoTitle, data) {
  if (typeof data !== 'object' || data === null || !('info' in data)) {
    return;
  }
  const messageData = data;
  // Handle player state changes (e.g., play, pause, end)
  if (typeof messageData.info?.playerState === 'number') {
    switch (messageData.info.playerState) {
      case 1:
        // Playing
        dispatchCustomEvent('videoPlay', {
          videoId,
          videoTitle,
          isPlaying: true
        });
        handleVideoStart(videoId, videoTitle);
        break;
      case 2: // Paused
      case 0:
        // Ended
        dispatchCustomEvent('videoPause', {
          videoId,
          videoTitle,
          isPlaying: false
        });
        handleVideoPauseOrEnd(videoId, videoTitle);
        break;
      case 5:
        // Cued
        dispatchCustomEvent('videoReady', {
          videoId,
          videoTitle,
          isReady: true
        });
        break;
      default:
        break;
    }
  }
  // Track current time and progress
  if (typeof messageData.info?.currentTime === 'number') {
    dispatchCustomEvent('videoProgress', {
      videoId,
      videoTitle,
      currentTime: messageData.info.currentTime
    });
  }
  // Track video duration
  if (typeof messageData.info?.duration === 'number') {
    dispatchCustomEvent('videoDuration', {
      videoId,
      videoTitle,
      duration: messageData.info.duration
    });
  }
}
function handleSolidTangoMessage(data) {
  if (typeof data !== 'object' || data === null || !('event' in data)) {
    return;
  }
  switch (data.func) {
    case 'ready':
      dispatchCustomEvent(`${data.id}_ready`, {
        videoId: data.id,
        isReady: data.args
      });
      break;
    case 'playing':
      dispatchCustomEvent(`${data.id}_playing`, {
        videoId: data.id,
        isPlaying: data.args
      });
      break;
    case 'paused':
      dispatchCustomEvent(`${data.id}_paused`, {
        videoId: data.id,
        isPlaying: data.args
      });
      break;
    case 'position':
      dispatchCustomEvent(`${data.id}_position`, {
        videoId: data.id,
        currentTime: data.args
      });
      break;
    case 'duration':
      dispatchCustomEvent(`${data.id}_duration`, {
        videoId: data.id,
        duration: data.args
      });
      break;
    case 'metadata':
      dispatchCustomEvent(`${data.id}_metadata`, {
        videoId: data.id,
        metadata: data.args
      });
      break;
    default:
      break;
  }
}
export function useVideoMessageHandler(video, videoOrigin) {
  const {
    type,
    title,
    videoId
  } = video;
  // Receive message event
  const receiveMessage = useCallback(event => {
    if (event.origin !== videoOrigin) return;
    try {
      const rawData = event.data;
      const data = typeof rawData === 'string' ? JSON.parse(rawData) : rawData;
      if (type === 'youtube') {
        handleYouTubeMessage(videoId, title || 'Youtube-video', data);
      } else {
        handleSolidTangoMessage(data);
      }
    } catch (error) {
      logger.warn('Error parsing message event data:', error);
    }
  }, [videoOrigin, type, videoId, title]);
  // Add event listener
  useEffect(() => {
    window.addEventListener('message', receiveMessage);
    return () => window.removeEventListener('message', receiveMessage);
  }, [receiveMessage]);
}